import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { calculateViewportBounds, isNodeOffScreen, calculateOffScreenIndicator, renderOffScreenIndicator, renderNodeWithLabel, renderLinkLabel, } from '../shared/graphRenderUtils';
export const GraphCanvas = React.forwardRef(({ graphData, width, height, selectedLink = null, selectedNode = null, onNodeClick, onLinkClick, onBackgroundClick, enableOffScreenIndicators = true, enableLinkLabels = true, backgroundColor = 'transparent', }, ref) => {
    const displayData = {
        nodes: graphData.nodes,
        links: graphData.links || graphData.edges,
    };
    // Get connected node IDs for the selected node
    const connectedNodeIds = React.useMemo(() => {
        if (!selectedNode)
            return new Set();
        const connected = new Set();
        connected.add(selectedNode);
        displayData.links.forEach((link) => {
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            if (sourceId === selectedNode) {
                connected.add(targetId);
            }
            if (targetId === selectedNode) {
                connected.add(sourceId);
            }
        });
        return connected;
    }, [selectedNode, displayData.links]);
    const nodeCanvasObject = useCallback((node, ctx, globalScale) => {
        const isDark = document.documentElement.classList.contains('dark');
        const isConnected = selectedNode ? connectedNodeIds.has(node.id) : false;
        const isSelected = node.id === selectedNode;
        renderNodeWithLabel(node, ctx, globalScale, isDark, isSelected, isConnected, selectedNode !== null);
    }, [selectedNode, connectedNodeIds]);
    const linkCanvasObject = useCallback((link, ctx, globalScale) => {
        if (!enableLinkLabels)
            return;
        const linkIdx = link.id?.toString().includes('rel-')
            ? parseInt(link.id.split('-')[1])
            : null;
        const isSelected = selectedLink !== null &&
            (linkIdx === selectedLink || link.id === selectedLink);
        // Check if link is connected to selected node
        const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
        const targetId = typeof link.target === 'object' ? link.target.id : link.target;
        const isConnectedToSelectedNode = selectedNode && (sourceId === selectedNode || targetId === selectedNode);
        const isDark = document.documentElement.classList.contains('dark');
        renderLinkLabel(link, ctx, globalScale, isDark, isSelected || !!isConnectedToSelectedNode);
    }, [selectedLink, selectedNode, enableLinkLabels]);
    const onRenderFramePost = useCallback((ctx, globalScale) => {
        if (!enableOffScreenIndicators || !ref || typeof ref === 'function')
            return;
        if (!ref.current)
            return;
        const zoom = ref.current.zoom();
        const centerPos = ref.current.centerAt();
        if (!centerPos)
            return;
        const viewport = calculateViewportBounds(centerPos, width, height, zoom);
        const offScreenNodes = [];
        graphData.nodes.forEach((node) => {
            if (isNodeOffScreen(node, viewport, node.size || 5, 25)) {
                offScreenNodes.push(node);
            }
        });
        const isDark = document.documentElement.classList.contains('dark');
        offScreenNodes.forEach((node) => {
            const offScreenIndicator = calculateOffScreenIndicator(node, viewport, 40, width, height, centerPos, zoom);
            renderOffScreenIndicator(ctx, offScreenIndicator, globalScale, isDark);
        });
    }, [enableOffScreenIndicators, ref, width, height, graphData.nodes]);
    return (_jsx(ForceGraph2D, { ref: ref, width: width, height: height, graphData: displayData, nodeId: "id", nodeLabel: (node) => node.label, nodeColor: (node) => node.color || '#999', nodeVal: (node) => node.size || 5, linkLabel: (link) => link.label, linkDirectionalArrowLength: (link) => {
            if (selectedLink === null)
                return 6;
            const linkIdx = link.id?.toString().includes('rel-')
                ? parseInt(link.id.split('-')[1])
                : null;
            return linkIdx === selectedLink || link.id === selectedLink ? 10 : 6;
        }, linkDirectionalArrowRelPos: 1, linkWidth: (link) => {
            const linkIdx = link.id?.toString().includes('rel-')
                ? parseInt(link.id.split('-')[1])
                : null;
            const isSelectedLink = selectedLink !== null && (linkIdx === selectedLink || link.id === selectedLink);
            // Check if link is connected to selected node
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            const isConnectedToSelectedNode = selectedNode && (sourceId === selectedNode || targetId === selectedNode);
            if (isSelectedLink || isConnectedToSelectedNode)
                return 4;
            if (selectedLink !== null || selectedNode !== null)
                return 1;
            return 2;
        }, linkColor: (link) => {
            const isDark = document.documentElement.classList.contains('dark');
            const linkIdx = link.id?.toString().includes('rel-')
                ? parseInt(link.id.split('-')[1])
                : null;
            const isSelectedLink = selectedLink !== null && (linkIdx === selectedLink || link.id === selectedLink);
            // Check if link is connected to selected node
            const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
            const targetId = typeof link.target === 'object' ? link.target.id : link.target;
            const isConnectedToSelectedNode = selectedNode && (sourceId === selectedNode || targetId === selectedNode);
            if (isSelectedLink || isConnectedToSelectedNode) {
                return isDark ? 'rgba(96, 165, 250, 0.9)' : 'rgba(59, 130, 246, 0.9)';
            }
            if (selectedLink !== null || selectedNode !== null) {
                return 'rgba(128, 128, 128, 0.15)';
            }
            return 'rgba(128, 128, 128, 0.4)';
        }, backgroundColor: backgroundColor, d3AlphaDecay: 0.02, d3VelocityDecay: 0.3, cooldownTicks: 100, warmupTicks: 50, enableNodeDrag: true, enableZoomInteraction: true, enablePanInteraction: true, onNodeClick: onNodeClick, onLinkClick: onLinkClick, onBackgroundClick: onBackgroundClick, nodeCanvasObject: nodeCanvasObject, linkCanvasObjectMode: () => 'after', linkCanvasObject: linkCanvasObject, onRenderFramePost: onRenderFramePost }));
});
GraphCanvas.displayName = 'GraphCanvas';
